import { AxiosResponse, AxiosInstance } from 'axios';
import { Store } from 'vuex';

import { MenuMetadataForChannel, MenuItemGroup, MenuItemGroupType, IngredientsMetadataForChannel, MENU_METADATA_CHANNELS, INGREDIENTS_METADATA_COUNTRIES, TopLevelMenuGroup } from '@/models/metadata';
import { AppState } from '@/store';
import { MUTATIONS as MENU_MUTATIONS } from '@/store/menu';
import { Menu, MenuItem } from '@/models';
import { isDefined } from '@/helpers/util';
import { arrayToMap } from '@/helpers/array';

import { ApiService } from '../api/api.service';
import { Logger } from '../logger/logger';
import { PromiseCacher } from '@/helpers/async';
import { isDesktop } from '@/helpers/util';
import { AnalyticsService } from '../analytics/analytics.service';

export interface MetadataService {
  getMenuMetadata(forceRefresh?: boolean): Promise<{ metadata: MenuMetadataForChannel; etag: string }>;
  setMenuMetadata(metadata?: MenuMetadataForChannel, etag?: string | null): void;
  refreshMenuMetadata(forceRefresh: boolean): Promise<MenuMetadataForChannel>;
  refreshMenuMetadataWithNutrition(forceRefresh: boolean): Promise<MenuMetadataForChannel>;
  getValidMenuGroupsForMenu(menu: Menu, metadata: MenuMetadataForChannel, topLevelOnly: boolean): MenuItemGroup[];
  getMenuItemGroupsForTopLevelMenu(metadata: MenuMetadataForChannel): MenuItemGroup[];
  resolveItemIdsInGroup(group: MenuItemGroup, metadata: MenuMetadataForChannel): string[];
  getFirstGroupContainingMenuItem(menuItemId: string): MenuItemGroup | null;
  getDisplayInputType(menuItemId: string): string;
  injectMetadata(metadataFragment: any): void;
  getIngredientsMetadata(): Promise<{ metadata: IngredientsMetadataForChannel; etag: string }>;
  setIngredientsMetadata(metadata: IngredientsMetadataForChannel, etag?: string | null): void;
  refreshIngredientsMetadata(forceRefresh: boolean): Promise<IngredientsMetadataForChannel>;
}

// How far we'll recurse to resolve a nested group before
// we give up since there could be infinite cycles that would lock the UI
const MAX_NESTED_GROUP_LOOKUP_DEPTH = 5;

export class AppMetadataService extends ApiService implements MetadataService {
  constructor(store: Store<AppState>, httpClient: AxiosInstance, logger: Logger, private promiseCacher: PromiseCacher, protected analytics: AnalyticsService) {
    super(store, httpClient, logger, analytics);
  }

  get channel(): string {
    return isDesktop(this.store.state) ? MENU_METADATA_CHANNELS.DESKTOP : MENU_METADATA_CHANNELS.MOBILE;
  }

  getMMDRegion() {
    //use the direct window variable for now since countryProfile.countryCode takes too long to fetch the variable
    const countryCode = window._cmgCountryCode
    if (countryCode.toUpperCase() === 'GB') {
      return 'UK';
    }
    if (countryCode.toUpperCase() === 'CA') {
      return 'CA';
    }
    return 'US'
  }

  getMenuMetadata(forceRefresh: boolean = false, includeNutrition: boolean = false) {
    const url = `menu-metadata/v1/menu-metadata${includeNutrition ? '/nutrition' : ''}?channel=${this.channel}&region=${this.getMMDRegion()}`;

    return this.promiseCacher.exec(`getMenuMetadata${includeNutrition ? 'WithNutrition' : ''}`, forceRefresh, async () => {
      const mmdCache = this.store.state.menu.cachedMetadata[`${this.channel}${includeNutrition ? '-nutrition' : ''}`];
      const etag = mmdCache?.etag;
      const cachedMetadata = mmdCache?.data;
      // return { metadata: (nutritionMMD as any) as MenuMetadataForChannel, etag: 'asdf' };
      if (cachedMetadata && etag) {
        const cacheResult: AxiosResponse<MenuMetadataForChannel> = await this.httpClient.get(url, {
          headers: {
            'If-None-Match': etag,
          },
          validateStatus: status => status === 200 || status === 304,
        });

        if (cacheResult.status == 304) {
          return { metadata: cachedMetadata, etag };
        } else if (cacheResult.status == 200) {
          if (includeNutrition) {
            cacheResult.data.nutrition = true;
          }
          return { metadata: cacheResult.data, etag: cacheResult.headers.etag };
        }
      }

      return await this.httpClient
        .get(url)
        .then((r: AxiosResponse<MenuMetadataForChannel>) => {
          if (includeNutrition) {
            r.data.nutrition = true;
          }
          return { metadata: r.data, etag: r.headers.etag };
        })
        .catch(e => this.logAndThrowApiError('getMenuMetadata', e));
    });
  }

  setMenuMetadata(metadata: MenuMetadataForChannel = {} as MenuMetadataForChannel, etag: string | null): void {
    this.store.commit(MENU_MUTATIONS.SET_METADATA, { metadata, etag });
  }

  async refreshMenuMetadata(forceRefresh: boolean = false) {
    const { metadata, etag } = await this.getMenuMetadata(forceRefresh, false);
    this.processMMD(metadata);

    this.setMenuMetadata(metadata, etag);
    return metadata;
  }

  async refreshMenuMetadataWithNutrition(forceRefresh: boolean = false) {
    const { metadata, etag } = await this.getMenuMetadata(forceRefresh, true);
    this.processMMD(metadata);

    await this.store.commit(MENU_MUTATIONS.SET_METADATA_WITH_NUTRITION, { metadata, etag });
    return metadata;
  }

  async refreshIngredientsMetadata(forceRefresh: boolean = false) {
    const { metadata, etag } = await this.getIngredientsMetadata(forceRefresh);

    this.setIngredientsMetadata(metadata, etag);
    return metadata;
  }

  async getIngredientsMetadata(forceRefresh: boolean = false) {
    const url = `menu-metadata/v1/menu-metadata/ingredients?channel=${this.channel}&region=${this.getMMDRegion()}`;

    return this.promiseCacher.exec('getIngredientsMetadata', forceRefresh, async () => {
      const ingredientsCache = this.store.state.menu.ingredientsMetadata[INGREDIENTS_METADATA_COUNTRIES.US];
      const etag = ingredientsCache?.etag;
      const cachedMetadata = ingredientsCache?.data;

      if (cachedMetadata && etag) {
        const cacheResult: AxiosResponse<IngredientsMetadataForChannel> = await this.httpClient.get(url, {
          headers: {
            'If-None-Match': etag,
          },
          validateStatus: status => status === 200 || status === 304,
        });

        if (cacheResult.status == 304) {
          return { metadata: cachedMetadata, etag };
        } else if (cacheResult.status == 200) {
          return { metadata: cacheResult.data, etag: cacheResult.headers.etag };
        }
      }

      return await this.httpClient
        .get(url)
        .then((r: AxiosResponse<IngredientsMetadataForChannel>) => {
          return { metadata: r.data, etag: r.headers.etag };
        })
        .catch(e => this.logAndThrowApiError('getMenuMetadata', e));
    });
  }

  setIngredientsMetadata(metadata: IngredientsMetadataForChannel, etag: string | null): void {
    this.store.commit(MENU_MUTATIONS.SET_INGREDIENTS_METADATA, { metadata, etag });
  }

  private processMMD(metadata: MenuMetadataForChannel) {
    if (!metadata.nutrition) {
      const nonVariantSections = [{ id: 'SingleSidesContentGroup', alias: 'Side' }, { id: 'Dessert', alias: 'Dessert' },
      { id: 'AllDrinkContentGroup', alias: 'Drink' }, { id: 'ChipsSidesContentGroup', alias: 'Side' }, { id: 'BYOAddOnContentGroup', alias: 'Side' }, {id: 'ExtraProteinSideContentGroup', alias: 'Side'}];
      const nonVariantSectionMap: { [key: string]: string } = {};
      nonVariantSections.forEach(s => {
        metadata?.itemSections[s.id]?.items.forEach(i => {
          nonVariantSectionMap[i.menuItemId] = s.alias;
        });
      });
      metadata.nonVariantSectionMap = nonVariantSectionMap;
    }

    metadata.dietaryTagsLookup = {};
    metadata.dietaryTagGroups?.forEach(dtg => {
      dtg.tags?.forEach(tag => {
        metadata.dietaryTagsLookup[tag.tagCode] = {
          tagGroupId: dtg.id,
          badgeText: tag.preferredBadgeText || dtg.badgeText,
          badgeColor: dtg.badgeColor,
          groupName: dtg.groupName
        };
      });
    });
    return metadata;
  }

  getValidMenuGroupsForMenu(menu: Menu, metadata: MenuMetadataForChannel, topLevelOnly: boolean): MenuItemGroup[] {
    const topLevelMenu = metadata.topLevelMenu;
    if (!topLevelMenu) {
      return [];
    }

    // Combine all items from menu and convert to a lookup for quick reference
    const menuItems = ((menu.entrees as readonly MenuItem[]) || []).concat(menu.sides || []).concat(menu.drinks || []);
    const menuItemMap = arrayToMap(menuItems, i => i.itemId);

    // Get groups from menu
    const groups = topLevelOnly ? this.getMenuItemGroupsForTopLevelMenu(metadata) : metadata.groups;

    // Filter out groups that are non-empty and do not have any menu items that satisfy the group
    return groups.filter(group => this.menuSatisfiesGroup(group, metadata, menuItemMap, 0, MAX_NESTED_GROUP_LOOKUP_DEPTH));
  }

  getMenuItemGroupsForTopLevelMenu(metadata: MenuMetadataForChannel): MenuItemGroup[] {
    const topLevelMenu = metadata.topLevelMenu;
    if (!topLevelMenu) {
      return [];
    }

    return topLevelMenu.groups.sort((a: TopLevelMenuGroup, b: TopLevelMenuGroup) => a.sortOrder - b.sortOrder).map(g => metadata.groups.find(gr => gr.id == g.menuItemGroupId)).filter(isDefined);
  }

  resolveItemIdsInGroup(group: MenuItemGroup, metadata: MenuMetadataForChannel, depth?: number, maxDepth?: number): string[] {
    if (depth === undefined) {
      depth = 0;
    }

    if (maxDepth === undefined) {
      maxDepth = MAX_NESTED_GROUP_LOOKUP_DEPTH;
    }

    if (depth > maxDepth) {
      this.logger.error('Max depth reached while expanding groups');
      return [];
    }

    switch (group.type) {
      case MenuItemGroupType.Default:
      case MenuItemGroupType.PreConfigured:
        return group.items.map(i => i.menuItemId);

      case MenuItemGroupType.Nested:
      case MenuItemGroupType.Quantity:
        return group.groups.reduce((acc, nestedGroupInfo) => {
          const nestedGroup = metadata.groups.find(mg => mg.id == nestedGroupInfo.menuItemGroupId);
          if (!nestedGroup) {
            this.logger.error(`Failed to find group with id '${nestedGroupInfo.menuItemGroupId}'`);
            return acc;
          }

          acc.push(...this.resolveItemIdsInGroup(nestedGroup, metadata, depth! + 1, maxDepth! + 1));

          return acc;
        }, [] as string[]);

      default:
        this.logger.warn('Unknown group type:', group);
        return [];
    }
  }

  getFirstGroupContainingMenuItem(menuItemId: string): MenuItemGroup | null {
    if (!this.store.state.menu.metadata) {
      return null;
    }

    const foundGroup = this.store.state.menu.metadata.groups.find(group => {
      if (group.type !== MenuItemGroupType.Default) {
        return false;
      }

      return !!group.items && !!group.items.find(item => item.menuItemId == menuItemId);
    });

    return foundGroup || null;
  }

  getDisplayInputType(itemId: string): string {
    const { metadata } = this.store.state.menu;
    let inputType = 'radio';

    if (metadata && metadata.itemSections && metadata.itemSections.BagItems) {
      const item = metadata.itemSections.BagItems.items.find(i => i.menuItemId === itemId);

      if (item && item.displayInputType) {
        inputType = item.displayInputType;
      }
    }

    return inputType;
  }

  injectMetadata(metadataFragment: any) {
    const currentMMD = this.store.state.menu.metadata;

    const keyProperty = [
      // high priorty first
      'sortOrder',
      'id',
      'defaultItemId',
    ];

    const fullOverwrite = ['customizations', 'tags', 'nutrition'];

    /**
     * Simple object check.
     * @param item
     * @returns {boolean}
     */
    const isObject = function (item: any) {
      return item && typeof item === 'object' && !Array.isArray(item);
    };

    const checkForKeyProperty = function (sourceObject: any) {
      for (let i = 0; i < keyProperty.length; i++) {
        if (sourceObject[keyProperty[i]] != undefined) {
          return keyProperty[i];
        }
      }
      return null;
    };

    /**
     * Deep merge two objects.
     * @param target
     * @param ...sources
     */
    const mergeDeep = function (target: any, ...sources: any[]): any {
      if (!sources.length) {
        return target;
      }
      const source = sources.shift();

      if (isObject(target) && isObject(source)) {
        for (const key in source) {
          if (isObject(source[key]) && !Array.isArray(source[key])) {
            if (!target[key]) {
              Object.assign(target, { [key]: {} });
            }
            mergeDeep(target[key], source[key]);
          } else if (Array.isArray(source[key])) {
            // merge array
            if (source[key].length === 0 || !isObject(source[key][0]) || fullOverwrite.includes(key)) {
              //full overwrite
              target[key] = source[key];
            } else {
              // overwrite array items by key
              const keyProperty = checkForKeyProperty(source[key][0]);

              if (keyProperty) {
                // find the matching item and mergeDeep
                source[key].forEach((e: any) => {
                  const targetArrayIndex = target[key].findIndex((targetElement: any) => targetElement[keyProperty] === e[keyProperty]);
                  mergeDeep(target[key][targetArrayIndex], e);
                });
              }
            }
          } else {
            Object.assign(target, { [key]: source[key] });
          }
        }
      }

      return mergeDeep(target, ...sources);
    };

    this.store.commit(MENU_MUTATIONS.SET_METADATA, {
      metadata: mergeDeep(currentMMD, metadataFragment),
      etag: this.store.state.menu.metadataETag,
    });
  }

  private menuSatisfiesGroup(
    group: MenuItemGroup,
    metadata: MenuMetadataForChannel,
    menuItemMap: { [id: string]: MenuItem | undefined },
    depth: number,
    maxDepth: number,
  ): boolean {
    if (depth > maxDepth) {
      this.logger.error('Max depth reached while expanding groups');

      return false;
    }

    switch (group.type) {
      case MenuItemGroupType.Default:
        return menuContainsItems(group.items);

      case MenuItemGroupType.Nested:
      case MenuItemGroupType.Quantity:
        return group.groups.every(g => {
          const nestedGroup = metadata.groups.find(mg => mg.id == g.menuItemGroupId);

          return !!nestedGroup && this.menuSatisfiesGroup(nestedGroup, metadata, menuItemMap, depth + 1, maxDepth);
        });

      case MenuItemGroupType.PreConfigured: {
        return true;
      }
      default:
        this.logger.warn('Unknown group type:', group);

        return false;
    }

    function menuContainsItems(items: { menuItemId: string }[]): boolean {
      if (!items.length) {
        return true;
      }

      return !!items.find(i => !!menuItemMap[i.menuItemId]);
    }
  }
}
